import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Merge, AlertTriangle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCuit } from '../lib/cuit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_STYLE = {
  'Mismo CUIT': 'bg-red-100 text-red-800 border-red-300',
  'Misma razón social': 'bg-amber-100 text-amber-800 border-amber-300',
  'Mismo código de proveedor': 'bg-amber-100 text-amber-800 border-amber-300',
  'Razón social muy parecida': 'bg-sky-100 text-sky-800 border-sky-300',
};

export default function SupplierMerge() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({});   // groupIdx -> { primary, absorbed: [] }
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/supplier-merge/candidates`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron buscar los duplicados');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== 'admin') {
    return <p className="text-muted-foreground" data-testid="merge-no-access">Sólo un administrador puede fusionar proveedores.</p>;
  }

  const sel = (gi) => selection[gi] || { primary: '', absorbed: [] };

  const setPrimary = (gi, id) => {
    const cur = sel(gi);
    setSelection({ ...selection, [gi]: { primary: id, absorbed: cur.absorbed.filter(a => a !== id) } });
  };

  const toggleAbsorbed = (gi, id) => {
    const cur = sel(gi);
    if (cur.primary === id) return;
    const absorbed = cur.absorbed.includes(id) ? cur.absorbed.filter(a => a !== id) : [...cur.absorbed, id];
    setSelection({ ...selection, [gi]: { ...cur, absorbed } });
  };

  const openPreview = async (gi) => {
    const cur = sel(gi);
    if (!cur.primary || cur.absorbed.length === 0) {
      toast.error('Elegí cuál queda y al menos uno para absorber');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/supplier-merge/preview`,
        { primary_id: cur.primary, absorbed_ids: cur.absorbed }, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo previsualizar la fusión');
    } finally { setBusy(false); }
  };

  const confirmMerge = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/supplier-merge`, {
        primary_id: preview.primary.supplier_id,
        absorbed_ids: preview.absorbed.map(a => a.supplier_id),
      }, { withCredentials: true });
      toast.success(`Fusión lista: ${r.data.moved_total} registro(s) movidos a ${r.data.primary_name}`);
      setPreview(null);
      setSelection({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo fusionar');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="supplier-merge-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Fusionar Proveedores</h1>
          <p className="text-muted-foreground">
            Unifica fichas repetidas sin perder el historial: todo pasa a la ficha que elijas.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/suppliers')} data-testid="merge-back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Proveedores
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Buscando duplicados entre los proveedores…</p>
      ) : !data?.groups?.length ? (
        <Card data-testid="merge-empty">
          <CardContent className="flex items-center gap-3 py-10 text-muted-foreground">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <span>
              No se encontraron proveedores repetidos entre los {data?.total_suppliers || 0} de la base
              (se compara el CUIT, la razón social y el código de proveedor).
            </span>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground" data-testid="merge-summary">
            {data.groups.length} grupo(s) para revisar sobre {data.total_suppliers} proveedores.
            Elegí <b>cuál queda</b> y tildá las fichas que se absorben. Las absorbidas no se borran:
            quedan archivadas apuntando a la principal.
          </p>

          {data.groups.map((g, gi) => {
            const cur = sel(gi);
            const distintosCuit = new Set(g.members.map(m => (m.tax_id || '').replace(/\D/g, ''))).size > 1;
            return (
              <Card key={gi} data-testid={`merge-group-${gi}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Badge variant="outline" className={REASON_STYLE[g.reason] || ''}>{g.reason}</Badge>
                    <span className="text-muted-foreground text-sm">{g.members.length} fichas</span>
                  </CardTitle>
                  {distintosCuit && (
                    <p className="flex items-center gap-1 text-xs text-amber-700" data-testid={`merge-group-warning-${gi}`}>
                      <AlertTriangle className="h-3 w-3" /> Ojo: tienen <b>CUIT distinto</b>, así que pueden ser
                      proveedores diferentes. Revisá bien antes de fusionar.
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {g.members.map((m) => (
                    <div
                      key={m.supplier_id}
                      className={`flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${cur.primary === m.supplier_id ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20' : cur.absorbed.includes(m.supplier_id) ? 'border-rose-300 bg-rose-50/60 dark:bg-rose-950/20' : ''}`}
                      data-testid={`merge-member-${m.supplier_id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium">
                          {m.business_name}{' '}
                          {!m.is_active && <Badge variant="outline" className="ml-1 text-[10px]">Inactivo</Badge>}
                        </p>
                        <p className="font-mono text-xs text-muted-foreground">
                          CUIT {formatCuit(m.tax_id)}{m.legacy_code ? ` · código ${m.legacy_code}` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {m.history_total === 0
                            ? 'Sin movimientos'
                            : Object.entries(m.history).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </p>
                        {m.cuit_warning && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> {m.cuit_warning}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant={cur.primary === m.supplier_id ? 'default' : 'outline'}
                          onClick={() => setPrimary(gi, m.supplier_id)}
                          data-testid={`merge-primary-${m.supplier_id}`}
                        >
                          {cur.primary === m.supplier_id ? 'Queda esta' : 'Que quede esta'}
                        </Button>
                        <Button
                          size="sm"
                          variant={cur.absorbed.includes(m.supplier_id) ? 'destructive' : 'outline'}
                          disabled={cur.primary === m.supplier_id}
                          onClick={() => toggleAbsorbed(gi, m.supplier_id)}
                          data-testid={`merge-absorb-${m.supplier_id}`}
                        >
                          {cur.absorbed.includes(m.supplier_id) ? 'Se absorbe' : 'Absorber'}
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <Button
                      onClick={() => openPreview(gi)}
                      disabled={busy || !cur.primary || cur.absorbed.length === 0}
                      data-testid={`merge-review-${gi}`}
                    >
                      <Merge className="mr-2 h-4 w-4" /> Revisar fusión
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirmar la fusión</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm" data-testid="merge-preview">
              <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-3 dark:bg-emerald-950/20">
                <p className="text-xs uppercase tracking-wide text-emerald-800">Queda esta ficha</p>
                <p className="font-medium">{preview.primary.business_name}</p>
                <p className="font-mono text-xs text-muted-foreground">CUIT {formatCuit(preview.primary.tax_id)}</p>
              </div>
              <div className="rounded-lg border border-rose-300 bg-rose-50/60 p-3 dark:bg-rose-950/20">
                <p className="text-xs uppercase tracking-wide text-rose-800">Se archivan y pasan su historial</p>
                {preview.absorbed.map(a => (
                  <p key={a.supplier_id}>
                    {a.business_name} <span className="font-mono text-xs text-muted-foreground">({formatCuit(a.tax_id)})</span>
                  </p>
                ))}
              </div>
              <div>
                <p className="font-medium">Se van a mover {preview.to_move_total} registro(s):</p>
                {preview.to_move_total === 0 ? (
                  <p className="text-muted-foreground">Las fichas absorbidas no tienen movimientos.</p>
                ) : (
                  <ul className="mt-1 list-inside list-disc text-muted-foreground">
                    {Object.entries(preview.to_move).map(([k, v]) => <li key={k}>{k}: <b>{v}</b></li>)}
                  </ul>
                )}
              </div>
              {Object.keys(preview.fields_to_fill || {}).length > 0 && (
                <div>
                  <p className="font-medium">Datos que se completan en la ficha que queda:</p>
                  <ul className="mt-1 list-inside list-disc text-muted-foreground">
                    {Object.entries(preview.fields_to_fill).map(([k, v]) => <li key={k}>{k}: <b>{String(v)}</b></li>)}
                  </ul>
                </div>
              )}
              <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                El nombre y el CUIT de las fichas absorbidas quedan guardados como alias en la ficha que
                queda, así podés seguir buscándolas. Esta acción no se puede deshacer sola.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Cancelar</Button>
            <Button onClick={confirmMerge} disabled={busy} data-testid="merge-confirm-btn">
              {busy ? 'Fusionando…' : 'Fusionar ahora'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
